import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/src/modules/auth/AuthProvider';
import { useAuthStore } from '@/src/modules/auth/useAuthStore';

type Props = {
  /**
   * Optional RBAC permission code (Phase 4 foundation).
   * When set, users lacking this permission are redirected to /dashboard.
   * Phase 5 will wire this to specific routes (analytics, partner, etc.).
   */
  permission?: string;
};

export function ProtectedRoute({ permission }: Props = {}) {
  const location = useLocation();
  const { user, hydrated } = useAuth();
  const storeHydrated = useAuthStore((s) => s.hydrated);
  const hasPermission = useAuthStore((s) =>
    permission ? s.permissions.includes(permission) : true,
  );

  // Wait for both Firebase auth and RBAC store hydration to prevent flicker.
  if (!hydrated || !storeHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface">
        <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (permission && !hasPermission) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}
